import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { getAdminData, putAdminData } from "../../apis";
import {
  adminParcelDiscountPath,
  adminParcelProductsPath,
} from "../../apis/config";
import type { AdminActiveEvent } from "../../interfaces/AdminEventProduct";
import type { AdminParcelProductRow } from "../../interfaces/AdminParcelProduct";
import type {
  ParcelDiscountExcludedProduct,
  ParcelDiscountPolicy,
  ParcelDiscountUpdatePayload,
} from "../../interfaces/ParcelDiscount";
import { queryId } from "../UrlValidation";
import {
  downloadParcelProductsExcel,
  parseParcelProductsExcel,
} from "../../utils/parcelProductExcel";
import { portalAbsoluteUrl } from "../../utils/eventPortalLink";
import { createPortalQrDataUrl } from "../../utils/portalQr";
import { saveAs } from "file-saver";

const PARCEL_SEARCH_PRODUCT_BASE = "/parcel/search-product";

function parcelSearchProductHref(
  urlCode: string | null | undefined,
): string | null {
  const code = urlCode != null ? String(urlCode).trim() : "";
  if (!code) return null;
  return `${PARCEL_SEARCH_PRODUCT_BASE}?${new URLSearchParams({
    [queryId]: code,
  }).toString()}`;
}

/**
 * 배송(택배) 상품 엑셀 일괄 설정 + 할인 정책
 */
type ParcelProductExcelPanelProps = {
  active: AdminActiveEvent | null;
  loadingActive: boolean;
};

export default function ParcelProductExcelPanel({
  active,
  loadingActive,
}: ParcelProductExcelPanelProps) {
  const parcelPortalHref = parcelSearchProductHref(active?.urlCode);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** 할인 기준 금액(원) */
  const [discountThreshold, setDiscountThreshold] = useState("0");
  /** 할인율(%) */
  const [discountRatePercent, setDiscountRatePercent] = useState("0");
  const [excludedProducts, setExcludedProducts] = useState<
    ParcelDiscountExcludedProduct[]
  >([]);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [qrModal, setQrModal] = useState<{
    dataUrl: string;
    url: string;
  } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadDiscount = useCallback(async (eventId: number) => {
    setDiscountLoading(true);
    setError(null);
    try {
      const res = await getAdminData<ParcelDiscountPolicy>(
        adminParcelDiscountPath(eventId),
      );
      if (!res.isSuccess || !res.result) {
        setDiscountThreshold("0");
        setDiscountRatePercent("0");
        setExcludedProducts([]);
        if (!res.isSuccess) {
          setError(res.message || "할인 설정을 불러오지 못했습니다.");
        }
        return;
      }
      setDiscountThreshold(String(Number(res.result.thresholdAmount) || 0));
      setDiscountRatePercent(String(Number(res.result.discountRate) || 0));
      setExcludedProducts(
        Array.isArray(res.result.excludedProducts)
          ? res.result.excludedProducts
          : [],
      );
    } catch (err: unknown) {
      setDiscountThreshold("0");
      setDiscountRatePercent("0");
      setExcludedProducts([]);
      setError(
        err instanceof Error
          ? err.message
          : "할인 설정 조회 중 오류가 발생했습니다.",
      );
    } finally {
      setDiscountLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active) {
      setDiscountThreshold("0");
      setDiscountRatePercent("0");
      setExcludedProducts([]);
      return;
    }
    void loadDiscount(active.id);
  }, [active, loadDiscount]);

  useEffect(() => {
    if (!qrModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setQrModal(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qrModal]);

  const handleQrOpen = async () => {
    if (!parcelPortalHref) {
      setError("활성 행사에 코드가 있어야 QR을 만들 수 있습니다.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const abs = portalAbsoluteUrl(parcelPortalHref);
      const dataUrl = await createPortalQrDataUrl(abs);
      setQrModal({ dataUrl, url: abs });
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "QR 이미지를 만들지 못했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleQrDownloadPng = async () => {
    if (!qrModal) return;
    const res = await fetch(qrModal.dataUrl);
    const blob = await res.blob();
    saveAs(blob, "qr-parcel-search-product.png");
  };

  const handleDownload = async () => {
    if (!active) return;
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const [productsRes, discountRes] = await Promise.all([
        getAdminData<AdminParcelProductRow[]>(
          adminParcelProductsPath(active.id),
        ),
        getAdminData<ParcelDiscountPolicy>(
          adminParcelDiscountPath(active.id),
        ),
      ]);
      if (!productsRes.isSuccess || !Array.isArray(productsRes.result)) {
        setError(productsRes.message || "배송 상품 목록을 불러오지 못했습니다.");
        return;
      }
      const excludedCodes = new Set(
        (discountRes.isSuccess && Array.isArray(discountRes.result?.excludedProducts)
          ? discountRes.result.excludedProducts
          : []
        ).map((p) => String(p.productCode).trim()),
      );
      const normalized = productsRes.result.map((row) => {
        const n = normalizeParcelRow(row);
        if (!n.isDiscountExcluded && excludedCodes.has(n.productCode.trim())) {
          return { ...n, isDiscountExcluded: true };
        }
        return n;
      });
      downloadParcelProductsExcel(normalized, `배송상품_${active.name}`);
      setMessage("엑셀 파일을 내려받았습니다.");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "다운로드 중 오류가 발생했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !active) return;
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      const products = await parseParcelProductsExcel(file);
      const res = await putAdminData<unknown>(
        adminParcelProductsPath(active.id),
        {
          products,
        },
      );
      if (!res.isSuccess) {
        setError(res.message || "업로드 반영에 실패했습니다.");
        return;
      }
      setMessage(
        `업로드한 ${products.length}건으로 배송 상품 데이터를 덮어썼습니다.`,
      );
      await loadDiscount(active.id);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "업로드 처리 중 오류가 발생했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDiscount = async () => {
    if (!active) return;
    setError(null);
    setMessage(null);

    const thresholdAmount = Number(discountThreshold);
    const discountRate = Number(discountRatePercent);
    if (!Number.isFinite(thresholdAmount) || thresholdAmount < 0) {
      setError("할인 기준 금액은 0 이상의 숫자여야 합니다.");
      return;
    }
    if (!Number.isFinite(discountRate) || discountRate < 0 || discountRate > 100) {
      setError("할인율은 0 이상 100 이하의 숫자여야 합니다.");
      return;
    }

    setBusy(true);
    try {
      const payload: ParcelDiscountUpdatePayload = {
        thresholdAmount,
        discountRate,
      };
      const res = await putAdminData<unknown>(
        adminParcelDiscountPath(active.id),
        payload,
      );
      if (!res.isSuccess) {
        setError(res.message || "할인 설정 저장에 실패했습니다.");
        return;
      }
      setMessage("할인 설정을 저장했습니다.");
      await loadDiscount(active.id);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "할인 설정 저장 중 오류가 발생했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  const inputsDisabled =
    busy || !active || loadingActive || discountLoading;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-left">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          배송 상품 &gt; 엑셀 업로드
        </h3>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          {parcelPortalHref ? (
            <>
              <a
                href={parcelPortalHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
              >
                해당 사이트로 이동
              </a>
              <button
                type="button"
                onClick={() => void handleQrOpen()}
                disabled={busy}
                className="inline-flex items-center rounded-md border border-violet-500 bg-white px-3 py-1.5 text-sm font-medium text-violet-800 hover:bg-violet-50 disabled:opacity-50"
              >
                QR 이미지
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled
                title="활성 행사에 코드가 있어야 합니다."
                className="rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-400 cursor-not-allowed"
              >
                해당 사이트로 이동
              </button>
              <button
                type="button"
                disabled
                title="활성 행사에 코드가 있어야 합니다."
                className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm text-slate-400 cursor-not-allowed"
              >
                QR 이미지
              </button>
            </>
          )}
        </div>
      </div>

      <p className="mt-3 text-sm text-slate-600">
        업로드 시 상품 코드가 없는 상품은 '주문'이 없는 건에 대해서만
        삭제됩니다.
      </p>

      <p className="mt-2 text-sm text-slate-500">
        상품코드, 가격, 이름, 표시유무(Y/N, 빈 칸은 미표시), 할인제외(Y/N, 빈
        칸·기본값 N).
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={busy || !active || loadingActive}
          className="rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          엑셀 다운로드
        </button>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => void handleFileChange(e)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy || !active || loadingActive}
            className="rounded-md border border-slate-400 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            엑셀 업로드 (덮어쓰기)
          </button>
        </div>
      </div>

      <div className="mt-6 border-t border-slate-200 pt-5">
        <h4 className="text-sm font-semibold text-slate-900">배송 상품 할인</h4>
        <p className="mt-1 text-sm text-slate-500">
          장바구니 합계가 기준 금액 이상일 때 적용할 할인율입니다. 기준 금액
          또는 할인율이 0이면 할인을 사용하지 않습니다. 할인 제외 상품은 엑셀
          「할인제외」 열(Y/N)로 지정합니다.
        </p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2 max-w-xl">
          <div>
            <label
              htmlFor="parcel-discount-threshold"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              할인 기준 금액 (원)
            </label>
            <input
              id="parcel-discount-threshold"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={discountThreshold}
              onChange={(e) => setDiscountThreshold(e.target.value)}
              placeholder="예: 500000"
              disabled={inputsDisabled}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
            />
          </div>
          <div>
            <label
              htmlFor="parcel-discount-rate"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              할인율 (%)
            </label>
            <input
              id="parcel-discount-rate"
              type="number"
              inputMode="decimal"
              min={0}
              max={100}
              step={0.1}
              value={discountRatePercent}
              onChange={(e) => setDiscountRatePercent(e.target.value)}
              placeholder="예: 10"
              disabled={inputsDisabled}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
            />
          </div>
        </div>
        <div className="mt-3">
          <button
            type="button"
            onClick={() => void handleSaveDiscount()}
            disabled={inputsDisabled}
            className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "저장 중…" : "할인 설정 저장"}
          </button>
        </div>
        {excludedProducts.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-slate-700 mb-1">
              할인 제외 상품 ({excludedProducts.length})
            </p>
            <ul className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700 divide-y divide-slate-200">
              {excludedProducts.map((p) => (
                <li key={p.id} className="px-3 py-2">
                  <span className="font-mono text-xs text-slate-500">
                    {p.productCode}
                  </span>{" "}
                  {p.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="mt-3 text-sm text-emerald-700" role="status">
          {message}
        </p>
      )}

      {qrModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="parcel-qr-modal-title"
          onClick={() => setQrModal(null)}
        >
          <div
            className="max-w-md rounded-lg bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              id="parcel-qr-modal-title"
              className="text-lg font-semibold text-slate-900 mb-3"
            >
              배송 상품 검색 페이지 QR
            </h4>
            <div className="flex justify-center bg-white">
              <img
                src={qrModal.dataUrl}
                alt="택배 상품 검색 페이지 QR 코드"
                className="rounded border border-slate-200 max-w-full h-auto"
              />
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => void handleQrDownloadPng()}
                className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-900"
              >
                PNG 저장
              </button>
              <button
                type="button"
                onClick={() => setQrModal(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function normalizeParcelRow(r: unknown): AdminParcelProductRow {
  const o = r as Record<string, unknown>;
  const priceRaw = o.price;
  let price = 0;
  if (typeof priceRaw === "number" && Number.isFinite(priceRaw)) {
    price = priceRaw;
  } else if (priceRaw != null) {
    const n = Number(String(priceRaw).replace(/,/g, ""));
    price = Number.isFinite(n) ? n : 0;
  }
  return {
    productCode: String(o.productCode ?? ""),
    price,
    name: String(o.name ?? ""),
    isSearchable: Boolean(o.isSearchable),
    isDiscountExcluded: Boolean(o.isDiscountExcluded),
  };
}
